<script setup>
import { computed, defineAsyncComponent, getCurrentInstance, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { getCurrencySymbol } from '@/utils/currency.js'
import { shortenString } from '@/utils/strings.js'

const BaseButton = defineAsyncComponent(() =>
  import(/* webpackChunkName: "components/base/buttons/BaseButton.vue" */ '@/components/base/buttons/BaseButton.vue')
)
const BackBtn = defineAsyncComponent(() =>
  import(/* webpackChunkName: "components/base/buttons/BackBtn.vue" */ '@/components/base/buttons/BackBtn.vue')
)
const Copy = defineAsyncComponent(() =>
  import(/* webpackChunkName: "components/base/clipboardCopy/copy.vue" */ '@/components/base/clipboardCopy/copy.vue')
)
const BaseLoader = defineAsyncComponent(() =>
  import(/* webpackChunkName: "components/base/BaseLoader.vue" */ '@/components/base/BaseLoader.vue')
)

const instance = getCurrentInstance()
const $http = instance.proxy.$http
const text = instance.proxy.text
const route = useRoute()
const router = useRouter()
const vuexStore = useStore()

const transaction = ref(null)
const loading = ref(false)
const errorMessage = ref('')
const refreshAnimating = ref(false)
const isWrongTransactionType = ref(false)

const authToken = computed(() => vuexStore.state.authToken)
const pageTitle = computed(() => (
  transaction.value
    ? text('wallet_withdrawal.transaction_title')
    : text('wallet_withdrawal.transaction_details_title')
))
const statusText = computed(() => {
  if (!transaction.value?.status) {
    return ''
  }
  return text(`transactions.list.status.${transaction.value.status}`)
})

function transactionAmount() {
  if (!transaction.value || transaction.value.amount === null || !transaction.value.currency_code) {
    return '-'
  }
  return `${parseFloat(transaction.value.amount)} ${getCurrencySymbol(transaction.value.currency_code)}`
}

async function fetchTransaction() {
  const hadTransaction = Boolean(transaction.value)
  loading.value = true
  refreshAnimating.value = true
  errorMessage.value = ''
  isWrongTransactionType.value = false

  try {
    const { data } = await $http.get(
      `/v2/wallet/transaction/${route.params.id}`,
      {
        headers: {
          Authorization: 'Token ' + authToken.value,
        },
        validateStatus: () => true,
      }
    )

    if (data?.result?.type === 'withdrawal') {
      transaction.value = data.result
    } else {
      isWrongTransactionType.value = Boolean(data?.result)
      errorMessage.value = isWrongTransactionType.value
        ? text('wallet_withdrawal.transaction_wrong_type')
        : data?.message || text('wallet_withdrawal.transaction_error')
    }
  } catch (error) {
    errorMessage.value = text('wallet_withdrawal.transaction_error')
  } finally {
    loading.value = false
    window.setTimeout(() => {
      refreshAnimating.value = false
    }, hadTransaction ? 300 : 0)
  }
}

function goHome() {
  router.push({ name: 'Home' })
}

onMounted(() => {
  fetchTransaction()
})
</script>

<template>
  <div class="withdrawal-transaction">
    <div class="withdrawal-head">
      <BackBtn @click="$router.back()" />
      <h2>{{ pageTitle }}</h2>
    </div>

    <BaseLoader v-if="loading && !transaction" />
    <div v-else-if="errorMessage && !transaction" class="empty-state">
      <div class="empty-state__message">{{ errorMessage }}</div>
      <BaseButton grayscale @click="goHome">
        <template #label>
          {{ text('common.actions.close') }}
        </template>
      </BaseButton>
    </div>
    <template v-else-if="transaction">
      <div class="status-row">
        <div class="status">
          {{ statusText }}
        </div>
        <button
          type="button"
          class="refresh-status-button"
          :class="{ loading: refreshAnimating }"
          :disabled="loading"
          :aria-label="text('wallet_withdrawal.refresh_status')"
          :title="text('wallet_withdrawal.refresh_status')"
          @click="fetchTransaction"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M20 12a8 8 0 1 1-2.34-5.66" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <path d="M20 4v6h-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>

      <div class="transaction-info">
        <div class="row">
          <div class="label">{{ text('transactions.list.details.transaction.id') }}</div>
          <div class="value transaction-id">
            {{ shortenString(transaction.id) }}
            <Copy :value="transaction.id" />
          </div>
        </div>
        <div class="row">
          <div class="label">{{ text('transactions.list.details.transaction.type') }}</div>
          <div class="value">{{ text('transactions.list.withdrawal') }}</div>
        </div>
        <div class="row">
          <div class="label">{{ text('transactions.list.details.transaction.amount') }}</div>
          <div class="value amount">{{ transactionAmount() }}</div>
        </div>
        <div class="row">
          <div class="label">{{ text('transactions.list.details.transaction.currency') }}</div>
          <div class="value">{{ transaction.currency_code }}</div>
        </div>
        <div class="row">
          <div class="label">{{ text('transactions.list.details.transaction.status') }}</div>
          <div class="value">{{ statusText }}</div>
        </div>
        <div class="row">
          <div class="label">{{ text('transactions.list.details.transaction.date') }}</div>
          <div class="value">{{ formatDateTime(transaction.created_at) }}</div>
        </div>
        <div v-if="transaction.withdrawal" class="row">
          <div class="label">{{ text('wallet_withdrawal.external_id') }}</div>
          <div class="value transaction-id">
            {{ shortenString(transaction.withdrawal) }}
            <Copy :value="transaction.withdrawal" />
          </div>
        </div>
      </div>

      <div v-if="errorMessage" class="error">{{ errorMessage }}</div>
      <div v-if="loading" class="loading-message">
        {{ text('wallet_withdrawal.transaction_loading') }}
      </div>

      <div class="actions">
        <BaseButton grayscale @click="goHome">
          <template #label>
            {{ text('common.actions.close') }}
          </template>
        </BaseButton>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.withdrawal-transaction {
  padding: 24px;
  min-height: 600px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  .withdrawal-head {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 4px;

    h2 {
      color: var(--text-color-primary);
      font-family: "Rubik", sans-serif;
      font-size: 21px;
      font-weight: 700;
      margin: 0;
    }
  }

  .status-row {
    align-self: center;
    display: grid;
    grid-template-columns: 32px auto 32px;
    align-items: center;
    gap: 8px;
  }

  .status {
    grid-column: 2;
    border-radius: 999px;
    background: var(--accent-bg);
    color: var(--text-color-primary-reverse);
    font-family: "Rubik", sans-serif;
    font-size: 14px;
    font-weight: 600;
    padding: 8px 14px;
  }

  .refresh-status-button {
    grid-column: 3;
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--border-color);
    border-radius: 50%;
    background: var(--element-bg);
    color: var(--text-color-secondary);
    cursor: pointer;
    transition: 0.2s ease all;

    &:hover:not(:disabled) {
      background: var(--element-bg-lighter);
      color: var(--text-color-primary);
    }

    &:disabled {
      cursor: default;
      opacity: 0.55;
    }

    &.loading svg {
      animation: refresh-status-spin 0.8s linear infinite;
    }
  }

  @keyframes refresh-status-spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }

  .transaction-info {
    display: flex;
    flex-direction: column;
    gap: 12px;
    border-radius: 8px;
    background: var(--element-bg);
    padding: 16px;
  }

  .row {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    color: var(--text-color-primary);
    font-family: "Rubik", sans-serif;
    font-size: 14px;
    line-height: 135%;
  }

  .label {
    color: var(--text-color-secondary);
  }

  .value {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    min-width: 0;
    text-align: right;
    word-break: break-word;
  }

  .amount {
    color: #F34B47;
    font-weight: 600;
  }

  .actions {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .error {
    color: var(--text-color-error);
    text-align: center;
    font-family: "Rubik", sans-serif;
    font-size: 16px;
    margin: 8px 0;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
    margin-top: 24px;

    &__message {
      color: var(--text-color-secondary);
      text-align: center;
      font-family: "Rubik", sans-serif;
      font-size: 16px;
      line-height: 135%;
    }
  }

  .loading-message {
    color: var(--text-color-secondary);
    text-align: center;
    font-family: "Rubik", sans-serif;
    font-size: 14px;
  }

  @media (max-width: 920px) {
    padding: 16px;
  }
}
</style>
